import { ADM_FORMAT_TRIM_MM, type ADM_FORMAT } from "campaign-builder/domain/constants";
import { MAX_PREVIEW_PAGES, type DesignPreviewPage } from "campaign-builder/design/useDesignUpload";

export type ArtworkValidator = (pages: DesignPreviewPage[], numPages: number) => string | null;

const MM_PER_PT = 25.4 / 72;
// Print PDFs routinely include 3 mm of bleed per edge; PDF authoring tools
// also drift by fractions of a millimeter, so the check accepts the trim
// size or the trim + bleed size, each within a small tolerance.
const BLEED_MM = 6;
const TOLERANCE_MM = 2;

const near = (actual: number, expected: number) => Math.abs(actual - expected) <= TOLERANCE_MM;

const matchesSize = (longSide: number, shortSide: number, expLong: number, expShort: number) =>
  (near(longSide, expLong) && near(shortSide, expShort)) ||
  (near(longSide, expLong + BLEED_MM) && near(shortSide, expShort + BLEED_MM));

/**
 * Returns a validator rejecting PDFs whose pages don't match the format's
 * trim size (PAD-32228 / spec invariant ArtworkMatchesSelectedFormat), or
 * null for formats without a configured trim size (US postcards keep the
 * legacy accept-anything behavior). Orientation-agnostic: pages are compared
 * long-side to long-side.
 */
export const getArtworkDimensionValidator = (format: string): ArtworkValidator | null => {
  const trim = ADM_FORMAT_TRIM_MM[format as ADM_FORMAT];
  if (!trim) return null;

  const expLong = Math.max(trim.widthMm, trim.heightMm);
  const expShort = Math.min(trim.widthMm, trim.heightMm);

  return (pages) => {
    for (const page of pages) {
      const widthMm = page.widthPt * MM_PER_PT;
      const heightMm = page.heightPt * MM_PER_PT;
      const longSide = Math.max(widthMm, heightMm);
      const shortSide = Math.min(widthMm, heightMm);
      if (!matchesSize(longSide, shortSide, expLong, expShort)) {
        return `Artwork does not match the selected format (expected ${trim.widthMm} × ${trim.heightMm} mm, page ${
          page.pageNumber
        } is ${Math.round(widthMm)} × ${Math.round(heightMm)} mm).`;
      }
    }
    return null;
  };
};

/**
 * An ADM design is front and back in one file. Only the first two pages were
 * rendered (and measured) while the whole file was uploaded, so a longer PDF
 * sailed past the checks above with pages nobody had seen. Counted on the
 * document, not the preview, and rejected before anything is stored.
 */
export const validateArtworkPageCount: ArtworkValidator = (_pages, numPages) =>
  numPages > MAX_PREVIEW_PAGES
    ? `This PDF has ${numPages} pages. Upload the front and back as a single ${MAX_PREVIEW_PAGES}-page file.`
    : null;

/** Everything an ADM upload is checked against: page count for every format, trim size where one is configured. */
export const getAdmArtworkValidator = (format: string): ArtworkValidator => {
  const dimensions = getArtworkDimensionValidator(format);
  return (pages, numPages) => validateArtworkPageCount(pages, numPages) ?? dimensions?.(pages, numPages) ?? null;
};
